import React from 'react';
import {
  Box, Paper, Typography, Chip, Divider, Stack, Grid, LinearProgress, Link,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { TrainingInsights, scoreColor, formatScore, barColor } from '../types';

interface Props {
  data: TrainingInsights['objectionFaqHandling'];
  /** Jump the conversation timeline to a given ISO timestamp. */
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

const ObjFaqHandlingSection: React.FC<Props> = ({ data, onNavigateToTimestamp }) => {
  if (!data) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Objection & FAQ Handling</Typography>
        <Chip
          label={formatScore(data.overallScore)}
          color={scoreColor(data.overallScore)}
          size="medium"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {data.overallSummary}
      </Typography>
      {/* Progress bar only when there's a real score — an N/A dimension
          (no items of this type in the track) has no bar to draw. */}
      {data.overallScore != null && (
        <LinearProgress
          variant="determinate"
          value={data.overallScore}
          color={barColor(data.overallScore)}
          sx={{ mb: 3, height: 8, borderRadius: 1 }}
        />
      )}

      <Stack spacing={2}>
        {data.items?.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
              <Chip
                label={item.itemType}
                size="small"
                color="info"
                variant="outlined"
              />
              <Chip
                label={`${item.handlingScore}%`}
                size="small"
                color={scoreColor(item.handlingScore)}
              />
              <Chip
                label={item.adherenceLabel}
                size="small"
                variant="outlined"
              />
              {item.timestampIso && onNavigateToTimestamp && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onNavigateToTimestamp(item.timestampIso!)}
                  sx={{ ml: 'auto' }}
                >
                  View in timeline &rarr;
                </Link>
              )}
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              {item.question}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {item.adherenceSummary}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Grid container spacing={2}>
              {item.whatWorked && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="success.main" fontWeight={600}>
                        What worked
                      </Typography>
                      <Typography variant="body2">{item.whatWorked}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {item.whatMissed && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <CancelIcon color="error" fontSize="small" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        What missed
                      </Typography>
                      <Typography variant="body2">{item.whatMissed}</Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

            {item.whatRepShouldHaveSaid && (
              <Box mt={1.5} p={1.5} bgcolor="info.50" borderRadius={1}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  Suggested response
                </Typography>
                <Typography variant="body2">
                  {item.whatRepShouldHaveSaid}
                </Typography>
              </Box>
            )}

            {item.pushbackHandling && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1.5}
              >
                <strong>Pushback handling:</strong> {item.pushbackHandling}
              </Typography>
            )}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

export default ObjFaqHandlingSection;
